import { useEffect, useState } from 'react';
import { Text, TextInput, View } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import Toast from 'react-native-toast-message';
import SheetModal from '@/components/General/SheetModal';
import DatePickerInput from '@/components/General/inputs/DatePickerInput';
import Callout from '@/components/General/Callout';
import { Mutation } from '@/utils/react-query/Mutations';
import { getAttentionReasonLabel } from '@/utils/constants/followUp';

/**
 * SnoozeAttentionSheet — reason-scoped attention snooze (spec §11.6).
 *
 * A snooze always has an end/review date AND a reason — both required by
 * spec. It suppresses ONE reason (optionally audience-scoped), never the
 * person: snoozing an attendance gap leaves contact/care flags and work
 * visible. Expiry returns the person to evaluation without fabricating a
 * new interaction.
 */
const SnoozeAttentionSheet = ({ isOpen, onClose, organizationID, personID, reason }) => {
    const [until, setUntil] = useState(null);
    const [snoozeReason, setSnoozeReason] = useState('');

    useEffect(() => {
        if (isOpen) {
            setUntil(null);
            setSnoozeReason('');
        }
    }, [isOpen]);

    const snoozeMutation = Mutation.followUp.snoozeAttention({
        onSuccess: () => {
            Toast.show({ type: 'success', text2: 'Attention reason snoozed' });
            onClose?.();
        },
        onError: (error) => {
            if (!error?.expected) {
                Toast.show({ type: 'error', text2: 'Failed to snooze. Please try again.' });
            }
        },
    });

    const submitting = snoozeMutation.isPending;
    const canSubmit = !!until && !!snoozeReason.trim() && !submitting;

    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);

    const handleSubmit = () => {
        if (!canSubmit) return;
        snoozeMutation.mutate({
            organizationID,
            personID,
            reason: reason?.reason,
            audienceID: reason?.audienceID ?? null,
            until: new Date(until).toISOString(),
            snoozeReason: snoozeReason.trim(),
        });
    };

    return (
        <SheetModal
            isOpen={isOpen}
            title="Snooze Attention Reason"
            subtitle={reason ? `${getAttentionReasonLabel(reason.reason)}${reason.audienceName ? ` · ${reason.audienceName}` : ''}` : undefined}
            cancelText="Cancel"
            confirmText="Snooze"
            onCancel={onClose}
            onClose={onClose}
            onConfirm={canSubmit ? handleSubmit : null}
            loading={submitting}
        >
            <View style={styles.body}>
                <Callout
                    type="info"
                    message="Snoozing hides only this reason until the review date. Other attention reasons and open care work stay visible."
                />

                <DatePickerInput
                    title="Snooze until (review date)"
                    mode="date"
                    required
                    value={until}
                    onUpdate={setUntil}
                    minimumDate={tomorrow}
                />

                <Text style={styles.label}>Reason <Text style={styles.required}>*</Text></Text>
                <TextInput
                    testID="snooze-reason-input"
                    style={styles.input}
                    multiline
                    numberOfLines={3}
                    placeholder="Why is this being snoozed? (e.g. hospitalized, out of town)..."
                    placeholderTextColor="#999"
                    value={snoozeReason}
                    onChangeText={setSnoozeReason}
                />
                <Text style={styles.hint}>
                    When the date passes the person becomes eligible for this flag again automatically — no new contact is implied.
                </Text>
            </View>
        </SheetModal>
    );
};

const styles = StyleSheet.create(({ colors }) => ({
    body: { paddingBottom: 16, gap: 8 },
    label: {
        fontSize: 13,
        color: colors.textSecondary,
        marginTop: 6,
        marginBottom: 2,
    },
    required: { color: colors.error },
    input: {
        fontSize: 14,
        color: colors.text,
        backgroundColor: colors.inputBackground,
        borderRadius: 10,
        padding: 12,
        textAlignVertical: 'top',
        minHeight: 64,
        _web: { outlineStyle: 'none' },
    },
    hint: {
        fontSize: 12,
        color: colors.textSecondary,
        fontStyle: 'italic',
        marginTop: 6,
    },
}));

export default SnoozeAttentionSheet;
